import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Input,
  Popover,
  FormControl,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import './Organisation.css';
import Layout from '../../../components/Layout';
import AdminNavbar from '../../../components/Admin/AdminNavbar/AdminNavbar';
import MultiplePersonIcon from '../../../assets/Admin/Organisation/multiple_person.svg';
import OrganisationTable from '../../../components/Admin/Organisation/OrganisationTable/OrganisationTable';
import { axiosInstance } from '../../../utils/tokenUtils';
import FilterIcon from '../../../assets/Admin/RequestImages/filter-icon.svg';
import ColoredFilterIcon from '../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import AddIcon from '@mui/icons-material/Add';
import OrganizationSkeleton from '../../../components/SkeletonEffect/OrganizationSkeleton/OrganizationSkeleton';
import usePagination from '@mui/material/usePagination';
import { toast } from 'react-hot-toast';

const Organisation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [organisationData, setOrganisationData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [openFilter, setOpenFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [rbacMe, setRbacMe] = useState(null);

  // Active filter states
  const [selectedCountry, setSelectedCountry] = useState('All');
  const [selectedCity, setSelectedCity] = useState('All');

  // Pending filter states - will be applied when user clicks Apply
  const [pendingCountry, setPendingCountry] = useState('All');
  const [pendingCity, setPendingCity] = useState('All');

  // Country and city lists for dropdowns
  const [countryList, setCountryList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState(null);

  // Whether there exists any organisation at all (unfiltered)
  const [hasAnyOrganisation, setHasAnyOrganisation] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  // Server-side pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Helper function to check if user has a specific permission
  const hasPermission = permission => {
    if (!rbacMe || !Array.isArray(rbacMe.permissions)) return false;
    return rbacMe.permissions.includes(permission);
  };

  // Check if current user can manage organisations
  const canManageOrganisations = () => {
    return hasPermission('manage_organisations') || (rbacMe?.roles || []).includes('super_admin');
  };

  // Load current user permissions
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let cachedData = null;
        try {
          const cached = sessionStorage.getItem('rbac_admin_me');
          if (cached) {
            cachedData = JSON.parse(cached);
            setRbacMe(cachedData);
          }
        } catch (_) {}

        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);

        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!rbacMe) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
  }, []);

  // Fetch countries from API
  const fetchCountries = async () => {
    try {
      const { data } = await axiosInstance.get('/auth/countries');
      setCountryList(data || []);
    } catch (error) {
      console.error('Error fetching countries', error);
      setCountryList([]);
    }
  };

  // Fetch cities for a specific country
  const fetchCities = async countryId => {
    if (!countryId) {
      setCityList([]);
      return;
    }

    try {
      const { data } = await axiosInstance.get(`/auth/countries/${countryId}/cities`);
      setCityList(data || []);
    } catch (error) {
      console.error('Error fetching cities', error);
      setCityList([]);
    }
  };

  // server-side fetch with query params and effect to refetch on changes
  const fetchOrganisations = async (params = {}) => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/admin/organisations', { params });
      const items =
        data?.results?.map(org => ({
          id: org.id,
          name: org.name || '---',
          email: org.email || '---',
          country: org.country || '---',
          city: org.city || '---',
          owner: org.owner_name || '---',
          totalInstructors: org.total_teachers || 0,
          createdOn: org.created_at ? formatDateForDisplay(org.created_at) : '---',
        })) || [];
      setOrganisationData(items);
      setTotalCount(data?.count || 0);
    } catch (error) {
      console.error('Error fetching organisations', error);
      setOrganisationData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // One-time unfiltered check to see if any organisation exists
  const checkAnyOrganisations = async () => {
    try {
      const { data } = await axiosInstance.get('/admin/organisations');
      const any = Array.isArray(data?.results) ? data.results.length > 0 : false;
      setHasAnyOrganisation(any);
    } catch (error) {
      console.error('Error checking organisations', error);
      setHasAnyOrganisation(false);
    } finally {
      setInitialCheckDone(true);
    }
  };

  // Fetch countries and check overall existence on mount
  useEffect(() => {
    fetchCountries();
    checkAnyOrganisations();
  }, []);

  // Trigger fetch when committed searchTerm or filters/page changes
  useEffect(() => {
    const params = { page, page_size: pageSize };
    if (searchTerm) params.search_query = searchTerm;
    if (selectedCountry !== 'All') params.country = selectedCountry;
    if (selectedCity !== 'All') params.city = selectedCity;
    fetchOrganisations(params);
  }, [searchTerm, selectedCountry, selectedCity, page, pageSize]);

  // Fetch cities when selected country changes
  useEffect(() => {
    if (selectedCountry !== 'All') {
      const country = countryList.find(c => c.name === selectedCountry);
      if (country) {
        setSelectedCountryId(country.id);
        fetchCities(country.id);
      }
    } else {
      setSelectedCountryId(null);
      setCityList([]);
    }
  }, [selectedCountry, countryList]);

  // Helper function to format date for display
  const formatDateForDisplay = dateString => {
    if (!dateString) return '---';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '---';
      return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    } catch (error) {
      return '---';
    }
  };

  const handleAddNew = () => {
    if (!canManageOrganisations()) {
      toast.dismiss();
      toast.error("You don't have permission to create organization");
      return;
    }
    navigate('/admin/organisation/create');
  };

  // Initialize pending filters when opening the filter modal
  const initializePendingFilters = () => {
    setPendingCountry(selectedCountry);
    setPendingCity(selectedCity);
  };

  // Apply pending filters to active filters
  const applyPendingFilters = () => {
    setSelectedCountry(pendingCountry);
    setSelectedCity(pendingCity);
  };

  const handleClickToOpenFilter = event => {
    setAnchorEl(event.currentTarget);
    setOpenFilter(true);
    initializePendingFilters();
  };

  const handleApplyFilter = () => {
    setOpenFilter(false);
    setAnchorEl(null);
    applyPendingFilters();
    setPage(1);
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
    setOpenFilter(false);
  };

  const handleClearAll = () => {
    setPendingCountry('All');
    setPendingCity('All');
  };

  // Handle country change in filter modal
  const handleCountryChange = countryName => {
    setPendingCountry(countryName);
    // Reset city when country changes
    if (countryName !== pendingCountry) {
      setPendingCity('All');
    }

    // Fetch cities for the selected country
    if (countryName !== 'All') {
      const country = countryList.find(c => c.name === countryName);
      if (country) {
        fetchCities(country.id);
      }
    } else {
      setCityList([]);
    }
  };

  // Function to get active filters
  const getActiveFilters = () => {
    const filters = [];

    if (selectedCountry !== 'All') {
      filters.push({
        type: 'country',
        label: `Country: ${selectedCountry}`,
        value: selectedCountry,
      });
    }

    if (selectedCity !== 'All') {
      filters.push({
        type: 'city',
        label: `City: ${selectedCity}`,
        value: selectedCity,
      });
    }

    return filters;
  };

  // Handler for removing a filter
  const handleRemoveFilter = filterType => {
    switch (filterType) {
      case 'country':
        setSelectedCountry('All');
        setPage(1);
        break;
      case 'city':
        setSelectedCity('All');
        setPage(1);
        break;
      default:
        break;
    }
  };

  const handleSearchKeyDown = e => {
    if (e.key === 'Enter') {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }
  };

  const handleRowsPerPageChange = event => {
    setPageSize(event.target.value);
    setPage(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const { items } = usePagination({
    count: totalPages,
    page,
    onChange: (event, value) => setPage(value),
  });

  return loading || !initialCheckDone ? (
    <Layout title="Klass Online | Organization">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <OrganizationSkeleton />
        </Box>
      </Box>
    </Layout>
  ) : (
    <Layout title="Klass Online | Organization">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          {hasAnyOrganisation ? (
            <Box className="flex flex-col p-[1rem] gap-4">
              <Box className="flex justify-between items-center">
                <Typography className="request-title">Manage & Disburse</Typography>
                <Box className="flex items-center gap-4 w-[40%]">
                  <Input
                    sx={{
                      textDecoration: 'none',
                      border: '1px solid var(--Stroke, #D5DEE3)',
                      borderRadius: '56px',
                      '&:before, &:after': {
                        borderBottom: 'none !important',
                      },
                      '&:hover:not(.Mui-disabled):before': {
                        borderBottom: 'none !important',
                      },
                      padding: '8px 10px',
                      '& .MuiInputBase-input': {
                        padding: '3px 5px 3px 5px',
                      },
                      background: '#FFF',
                      width: '100%',
                      height: '40px',
                    }}
                    type="search"
                    placeholder="Search Name or Email"
                    aria-label="Search"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    startAdornment={<SearchIcon className="search-icon-css" />}
                  />
                  {openFilter ? (
                    <img
                      src={ColoredFilterIcon}
                      alt="Filter"
                      style={{ width: '25px', height: '25px', cursor: 'pointer' }}
                      onClick={handleClickToOpenFilter}
                    />
                  ) : (
                    <img
                      src={FilterIcon}
                      alt="Filter"
                      style={{ width: '25px', height: '25px', cursor: 'pointer' }}
                      onClick={handleClickToOpenFilter}
                    />
                  )}
                  <button type="button" className="add-new-btn-header" onClick={handleAddNew}>
                    <AddIcon sx={{ color: '#fff', fontSize: 18 }} />
                    <Typography className="add-new-btn-text">Add New</Typography>
                  </button>
                </Box>
              </Box>

              {/* Filter Modal */}
              <Popover
                open={openFilter}
                anchorEl={anchorEl}
                onClose={handleCloseFilter}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  className: 'filter-modal-container',
                }}
              >
                {/* Header */}
                <Box className="filter-modal-header">
                  <Typography className="filter-modal-title">Filters</Typography>
                </Box>

                {/* Scrollable Content */}
                <Box className="filter-modal-content">
                  {/* Grid for filter items */}
                  <Box className="filter-grid">
                    {/* Country Filter */}
                    <Box className="filter-item">
                      <label className="filter-label">Country</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={pendingCountry}
                          onChange={e => handleCountryChange(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                                maxHeight: 200,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          <MenuItem value="All" className="dropdown-option">
                            All
                          </MenuItem>
                          {countryList.map(country => (
                            <MenuItem
                              key={country.id}
                              value={country.name}
                              className="dropdown-option"
                            >
                              {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* City Filter */}
                    <Box className="filter-item">
                      <label className="filter-label">City</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={pendingCity}
                          onChange={e => setPendingCity(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                                maxHeight: 200,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          <MenuItem value="All" className="dropdown-option">
                            All
                          </MenuItem>
                          {cityList.map(city => (
                            <MenuItem key={city.id} value={city.name} className="dropdown-option">
                              {city.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>

                {/* Buttons (Sticky Footer) */}
                <Box className="filter-buttons-container">
                  <Button
                    variant="outlined"
                    onClick={handleClearAll}
                    className="filter-clear-button"
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilter}
                    className="filter-apply-button"
                  >
                    Apply
                  </Button>
                </Box>
              </Popover>

              {/* Active Filters Display */}
              {getActiveFilters().length > 0 && (
                <Box className="active-filters-container">
                  {getActiveFilters().map((filter, index) => (
                    <Box key={index} className="filter-card">
                      <Typography className="filter-card-text">{filter.label}</Typography>
                      <CloseIcon
                        className="filter-card-close"
                        onClick={() => handleRemoveFilter(filter.type)}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              <OrganisationTable data={organisationData} />

              {/* Pagination Footer */}
              <Box
                className="organisation-pagination-container"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  <Typography className="organisation-pagination-text">Showing</Typography>
                  <Select
                    value={pageSize}
                    onChange={handleRowsPerPageChange}
                    className="organisation-pagination-select"
                    sx={{
                      height: '30px',
                      minWidth: '20px',
                      '& .MuiSelect-select': {
                        padding: '6px 4px 4px 8px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                  >
                    {[10, 15, 20, 30].map(num => (
                      <MenuItem key={num} value={num}>
                        {num}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography className="organisation-pagination-text">
                    of {totalCount} organisations
                  </Typography>
                </Box>
                <nav>
                  <ul
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: '10px 0',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                      let children = null;
                      if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                        children = <span className="organisation-pagination-ellipsis">…</span>;
                      } else if (type === 'page') {
                        children = (
                          <button
                            type="button"
                            className="organisation-pagination-button"
                            style={{
                              fontWeight: selected ? 'bold' : undefined,
                              margin: '0 5px',
                              padding: '5px 10px',
                              cursor: 'pointer',
                              backgroundColor: selected ? '#f0f0f0' : 'transparent',
                              border: 'none',
                              borderRadius: '4px',
                            }}
                            {...item}
                          >
                            {itemPage}
                          </button>
                        );
                      } else {
                        children = (
                          <button
                            type="button"
                            className="organisation-pagination-button"
                            style={{
                              margin: '0 5px',
                              padding: '5px 10px',
                              cursor: 'pointer',
                              border: 'none',
                              backgroundColor: 'transparent',
                            }}
                            {...item}
                          >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </button>
                        );
                      }
                      return <li key={index}>{children}</li>;
                    })}
                  </ul>
                </nav>
              </Box>
            </Box>
          ) : (
            <Box className="create-organisation-wrapper">
              <img src={MultiplePersonIcon} alt="Create Organization" />
              <Typography className="create-org-title">Create Organization</Typography>
              <Typography className="create-org-subtitle">
                Create and manage a group of teachers under one organization for better coordination
                and control.
              </Typography>
              <button type="button" className="add-new-btn" onClick={handleAddNew}>
                <AddIcon sx={{ color: '#fff', fontSize: 18 }} />
                Add New
              </button>
            </Box>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default Organisation;
